"use client";

import { useState } from "react";
import { Edit, Trash2, Eye, Heart, Users } from "lucide-react";
import type { Job } from "@/types";

interface JobListItemProps {
  job: Job;
  onEdit: (job: Job) => void;
  onDelete: (jobId: string) => void;
  onToggleActive: (jobId: string, active: boolean) => void;
}

export function JobListItem({ job, onEdit, onDelete, onToggleActive }: JobListItemProps) {
  const [isTogglingActive, setIsTogglingActive] = useState(false);

  const handleToggleActive = async () => {
    setIsTogglingActive(true);
    try {
      await onToggleActive(job._id, !job.active);
    } finally {
      setIsTogglingActive(false);
    }
  };

  const getJobTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      "full-time": "Heltid",
      "part-time": "Deltid",
      contract: "Vikariat",
      internship: "Prosjekt",
    };
    return labels[type] || type;
  };

  return (
    <div
      className="rounded-lg p-4 transition-all"
      style={{
        backgroundColor: "var(--color-bg-secondary)",
        border: "1px solid var(--color-border)",
        opacity: job.active ? 1 : 0.6,
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          {/* Title and Status */}
          <div className="mb-2 flex items-center gap-2">
            <h3
              style={{
                fontSize: "var(--font-size-body-lg)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--color-text-primary)",
              }}
            >
              {job.title}
            </h3>
            <span
              className="rounded-full px-2 py-0.5 text-xs font-medium"
              style={{
                backgroundColor: job.active
                  ? "rgba(74, 222, 128, 0.2)"
                  : "rgba(156, 163, 175, 0.2)",
                color: job.active ? "#4ade80" : "#9ca3af",
              }}
            >
              {job.active ? "Aktiv" : "Inaktiv"}
            </span>
          </div>

          {/* Location and Type */}
          <div className="mb-3 flex flex-wrap gap-2 text-sm" style={{ color: "var(--color-text-secondary)" }}>
            <span>{job.location}</span>
            <span>•</span>
            <span>{getJobTypeLabel(job.jobType)}</span>
            {job.salaryRange && (
              <>
                <span>•</span>
                <span>
                  {job.salaryRange.min.toLocaleString()} - {job.salaryRange.max.toLocaleString()} {job.salaryRange.currency}
                </span>
              </>
            )}
          </div>

          {/* Stats */}
          <div className="flex gap-4 text-sm" style={{ color: "var(--color-text-secondary)" }}>
            <div className="flex items-center gap-1">
              <Eye className="h-4 w-4" />
              <span>{job.viewCount} visninger</span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              <span>{job.swipeCount} swipes</span>
            </div>
            <div className="flex items-center gap-1">
              <Heart className="h-4 w-4" />
              <span>{job.matchCount} matches</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {/* Toggle Active/Inactive */}
          <button
            onClick={handleToggleActive}
            disabled={isTogglingActive}
            className="rounded-lg px-3 py-1.5 text-sm font-medium transition-colors"
            style={{
              backgroundColor: job.active
                ? "rgba(239, 68, 68, 0.1)"
                : "rgba(74, 222, 128, 0.1)",
              color: job.active ? "#ef4444" : "#4ade80",
            }}
          >
            {isTogglingActive ? "..." : job.active ? "Deaktiver" : "Aktiver"}
          </button>

          <div className="flex gap-1">
            {/* Edit */}
            <button
              onClick={() => onEdit(job)}
              className="rounded-lg p-2 transition-colors hover:bg-gray-700"
              title="Rediger"
            >
              <Edit className="h-4 w-4" style={{ color: "var(--color-text-secondary)" }} />
            </button>

            {/* Delete */}
            <button
              onClick={() => {
                if (confirm("Er du sikker på at du vil slette denne stillingen?")) {
                  onDelete(job._id);
                }
              }}
              className="rounded-lg p-2 transition-colors hover:bg-red-900/20"
              title="Slett"
            >
              <Trash2 className="h-4 w-4" style={{ color: "#ef4444" }} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

